import React, { useState, useEffect } from 'react';
import { Activity, AlertTriangle, Bug, CheckCircle, RefreshCcw, Power, ListChecks, ChevronLeft, ChevronRight, ChevronDown, Copy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

// 简易内联的 Card 组件以兼容
const Card = ({ children, className = '' }) => <div className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden ${className}`}>{children}</div>;
const CardHeader = ({ children, className = '' }) => <div className={`p-6 pb-0 ${className}`}>{children}</div>;
const CardTitle = ({ children, className = '' }) => <h3 className={`font-semibold tracking-tight ${className}`}>{children}</h3>;
const CardContent = ({ children, className = '' }) => <div className={`p-6 ${className}`}>{children}</div>;

const TaskFlowVisualizer = ({ status, fetchMode }) => {
  const steps = [
    { label: '系统派单' },
    { label: fetchMode === 'api' ? 'API节点抓取' : 'RPA节点抓取' },
    { label: 'AI清洗分析' },
    { label: '任务结单' }
  ];

  let currentStepIdx = 0;
  if (['completed', 'analyzed', 'failed', 'cancelled', 'stopped', 'analyzed_failed'].includes(status)) currentStepIdx = 3;
  else if (status === 'analyzing') currentStepIdx = 2;
  else if (status === 'running') currentStepIdx = 1;
  else currentStepIdx = 0; // pending, scheduled, paused

  const isError = ['failed', 'cancelled', 'stopped', 'analyzed_failed'].includes(status);
  
  return (
    <div className="w-full max-w-[280px] relative py-2">
      <div className="absolute left-6 right-6 top-[13px] h-0.5 bg-slate-200 dark:bg-slate-700 z-0"></div>
      <div 
        className={`absolute left-6 top-[13px] h-0.5 z-0 transition-all duration-500 ${isError ? 'bg-red-400' : 'bg-emerald-400'}`} 
        style={{ width: `calc(${(currentStepIdx / 3) * 100}% - ${24 * (currentStepIdx / 3)}px)` }}
      ></div>
      
      <div className="flex justify-between relative z-10">
        {steps.map((step, idx) => {
          const isPast = idx < currentStepIdx;
          const isCurrent = idx === currentStepIdx;
          const isErrorStep = isCurrent && isError;
          
          let bgColor = 'bg-slate-200 dark:bg-slate-700 text-slate-400';
          if (isPast) bgColor = 'bg-emerald-500 text-white';
          if (isCurrent && !isErrorStep) bgColor = 'bg-blue-500 text-white ring-4 ring-slate-50 dark:ring-slate-800';
          if (isCurrent && isErrorStep) bgColor = 'bg-red-500 text-white ring-4 ring-slate-50 dark:ring-slate-800';

          return (
            <div key={idx} className="flex flex-col items-center gap-1.5 w-[60px]">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm ${bgColor} z-10 transition-colors`}>
                {isPast ? '✓' : (idx + 1)}
              </div>
              <div className={`text-[10px] text-center w-[70px] -ml-[5px] scale-90 whitespace-nowrap ${isCurrent ? (isErrorStep ? 'text-red-600 dark:text-red-400 font-bold' : 'text-blue-600 dark:text-blue-400 font-bold') : 'text-slate-500 dark:text-slate-400'}`}>
                {step.label}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
};

export default function OpsDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({
    metrics: { activeTasks: 0, waitingRpa: 0, analyzing: 0, alerts: 0 },
    radar: [],
    matrix: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('radar'); // 'radar' or 'matrix'
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [expandedMatrixTasks, setExpandedMatrixTasks] = useState({});
  const [expandedRadarTasks, setExpandedRadarTasks] = useState({});
  const toggleRadarTaskExpand = (taskId) => {
    setExpandedRadarTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };
  const pageSize = 20;

  const toggleTaskExpand = (taskId) => {
    setExpandedMatrixTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const [filterProject, setFilterProject] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/ops/monitor-dashboard`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setSelectedAlerts([]); // Reset selections on refresh
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const rawAlerts = data.radar || [];
  
  const uniqueProjects = [...new Set(rawAlerts.map(item => item.projectName))].sort();

  const allAlerts = rawAlerts.filter(item => {
    let matchProject = filterProject === 'all' || item.projectName === filterProject;
    let matchType = true;
    if (filterType === 'rpa') matchType = item.rpaStuckCount > 0;
    else if (filterType === 'ai') matchType = item.aiStuckCount > 0;
    else if (filterType === 'ai_failed') matchType = item.aiFailedCount > 0;
    else if (filterType === 'bug') matchType = item.isProgressBug;
    
    return matchProject && matchType;
  });

  const totalPages = Math.max(1, Math.ceil(allAlerts.length / pageSize));
  const currentAlerts = allAlerts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedAlerts([]);
  }, [filterProject, filterType]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAlerts(currentAlerts.map(a => a.id));
    } else {
      setSelectedAlerts([]);
    }
  };

  const handleSelectOne = (taskId) => {
    const exists = selectedAlerts.includes(taskId);
    if (exists) {
      setSelectedAlerts(selectedAlerts.filter(id => id !== taskId));
    } else {
      setSelectedAlerts([...selectedAlerts, taskId]);
    }
  };

  const handleBatchAction = async (actionType) => {
    if (selectedAlerts.length === 0) return;
    if (!window.confirm(`确定要批量处理这 ${selectedAlerts.length} 个任务的异常记录吗？\n${actionType === 'reset' ? '将被退回排队重试' : '将被直接强制结单'}`)) return;

    try {
      if (actionType === 'reset') {
        await fetch(`${API_URL}/ops/monitor-dashboard/reset-task-anomalies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ taskIds: selectedAlerts })
        });
            } else if (actionType === 'retry-failed') {
        await fetch(`${API_URL}/ops/monitor-dashboard/retry-failed-analysis`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ taskIds: selectedAlerts })
        });
      } else if (actionType === 'force-complete') {
        await fetch(`${API_URL}/ops/monitor-dashboard/force-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ taskIds: selectedAlerts })
        });
      }
      fetchData();
    } catch(e) {}
  };

  const handleReset = async (taskId) => {
    if (!window.confirm("确定要重置该任务下所有异常的子记录，使其重新排队吗？")) return;
    try {
      const res = await fetch(`${API_URL}/ops/monitor-dashboard/reset-task-anomalies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ taskIds: [taskId] })
      });
      if (res.ok) fetchData();
    } catch(e) {}
  };

    const handleRetryFailed = async (taskId) => {
    if (!window.confirm("确定要清洗这些任务的过长文本，并直接重试解析吗？")) return;
    try {
      const res = await fetch(`${API_URL}/ops/monitor-dashboard/retry-failed-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ taskIds: [taskId] })
      });
      if (res.ok) fetchData();
    } catch(e) {}
  };

  const handleForceComplete = async (taskId) => {
    if (!window.confirm("确定要强制标记此任务为 100% 吗？忽略的记录将视为失败。")) return;
    try {
      const res = await fetch(`${API_URL}/ops/monitor-dashboard/force-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ taskId })
      });
      if (res.ok) fetchData();
    } catch(e) {}
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-gray-300';
      case 'scheduled': return 'bg-teal-500';
      case 'running': return 'bg-blue-500';
      case 'analyzing': return 'bg-purple-500';
      case 'analyzed':
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      case 'analyzed_failed': return 'bg-indigo-500';
      case 'paused': return 'bg-amber-500';
      default: return 'bg-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return '排队中';
      case 'scheduled': return '已排期';
      case 'running': return '抓取中';
      case 'analyzing': return 'AI解析中';
      case 'analyzed': return '已解析';
      case 'completed': return '已完成';
      case 'failed': return '已失败';
      case 'cancelled':
      case 'stopped': return '已取消';
      case 'paused': return '已暂停';
      case 'analyzed_failed': return 'AI解析失败';
      default: return status;
    }
  };

  if (user?.role !== 'ADMIN') return <div className="p-8">无权访问</div>;

  return (
    <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">全局任务调度监控大盘 (Ops Dashboard)</h1>
        <button onClick={fetchData} className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          刷新数据
        </button>
      </div>

      {/* Module 1: Core Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg"><Activity className="w-8 h-8" /></div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">活跃任务总数 (Active)</p>
              <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{data.metrics.activeTasks}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg"><RefreshCcw className="w-8 h-8" /></div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">爬虫等待中 (Waiting RPA)</p>
              <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{data.metrics.waitingRpa}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg"><Bug className="w-8 h-8" /></div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">AI 解析中 (Analyzing)</p>
              <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{data.metrics.analyzing}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg"><AlertTriangle className="w-8 h-8" /></div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">异常 / 阻塞预警 (Alerts)</p>
              <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{data.metrics.alerts}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-slate-200 dark:border-slate-700 flex space-x-6 mt-8">
        <button
          onClick={() => { setActiveTab('radar'); setCurrentPage(1); }}
          className={`pb-3 font-medium transition-colors flex items-center gap-2 ${activeTab === 'radar' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
        >
          <AlertTriangle className="w-4 h-4" />
          异常卡点雷达 ({allAlerts.length})
        </button>
        <button
          onClick={() => setActiveTab('matrix')}
          className={`pb-3 font-medium transition-colors flex items-center gap-2 ${activeTab === 'matrix' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
        >
          <ListChecks className="w-4 h-4" />
          全域任务矩阵 (最近 24 小时)
        </button>
      </div>

      {/* Tab Content: Radar */}
      {activeTab === 'radar' && (
        <Card className="border-red-100 dark:border-red-900/30 shadow-sm">
          <CardHeader className="bg-red-50/50 dark:bg-red-900/10 border-b border-red-100 dark:border-red-900/30 flex flex-col gap-4">
            <div className="flex flex-row justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertTriangle className="w-5 h-5" />
                异常任务列表 ({allAlerts.length} 个任务受阻)
              </CardTitle>
              {selectedAlerts.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={() => handleBatchAction('reset')} className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2 shadow-sm transition-colors">
                    <RefreshCcw className="w-4 h-4" />
                    批量重置异常 ({selectedAlerts.length})
                  </button>
                  <button onClick={() => handleBatchAction('retry-failed')} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2 shadow-sm transition-colors">
                    <RefreshCcw className="w-4 h-4" />
                    批量清洗并重解析
                  </button>
                  <button onClick={() => handleBatchAction('force-complete')} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm flex items-center gap-2 shadow-sm transition-colors">
                    <CheckCircle className="w-4 h-4" />
                    批量强制结单 ({selectedAlerts.length})
                  </button>
                </div>
              )}
            </div>
            
            {/* Filters */}
            <div className="flex items-center justify-between bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">按所属项目筛选:</label>
                  <select 
                    value={filterProject} 
                    onChange={e => setFilterProject(e.target.value)}
                    className="border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm py-1.5 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">全部项目</option>
                    {uniqueProjects.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300">按异常类型筛选:</label>
                  <select 
                    value={filterType} 
                    onChange={e => setFilterType(e.target.value)}
                    className="border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm py-1.5 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="all">全部异常类型</option>
                    <option value="rpa">⚠️ 仅看 RPA 超时</option>
                    <option value="ai">⚠️ 仅看 AI 死锁</option>
                    <option value="ai_failed">🚨 仅看 AI 解析失败</option>
                    <option value="bug">⚠️ 仅看 进度条伪差</option>
                  </select>
                </div>
              </div>
              <button 
                onClick={() => setSelectedAlerts(allAlerts.map(a => a.id))} 
                className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline flex items-center gap-1"
              >
                <ListChecks className="w-4 h-4" />
                一键全选当前筛选的 {allAlerts.length} 项
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {allAlerts.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">当前系统运行健康，无阻塞任务。</div>
            ) : (
              <div>
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-b dark:border-slate-700">
                    <tr>
                      <th className="p-4 font-medium w-12 text-center">
                        <input 
                          type="checkbox" 
                          className="rounded border-slate-300 dark:border-slate-600"
                          checked={selectedAlerts.length === currentAlerts.length && currentAlerts.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="p-4 font-medium">所属项目</th>
                      <th className="p-4 font-medium">任务编号</th>
                      <th className="p-4 font-medium">异常诊断</th>
                      <th className="p-4 font-medium">任务创建时间</th>
                      <th className="p-4 font-medium text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-slate-700 text-slate-700 dark:text-slate-300">
                    {currentAlerts.map(item => {
                      const isSelected = selectedAlerts.includes(item.id);
                      
                      return (
                        <tr key={item.id} className={isSelected ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}>
                          <td className="p-4 text-center">
                            <input 
                              type="checkbox" 
                              className="rounded border-slate-300 dark:border-slate-600 cursor-pointer"
                              checked={isSelected}
                              onChange={() => handleSelectOne(item.id)}
                            />
                          </td>
                          <td className="p-4 font-medium">{item.projectName}</td>
                          <td className="p-4">
                            <div className="flex items-center gap-2 cursor-pointer group" onClick={() => toggleRadarTaskExpand(item.id)}>
                                <div className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{item.taskNo}</div>
                                {item.fetchMode === 'rpa' && item.mingxiTaskIds && item.mingxiTaskIds.length > 0 && (
                                    <span className="text-slate-400">
                                        {expandedRadarTasks[item.id] ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
                                    </span>
                                )}
                            </div>
                            {item.fetchMode === 'rpa' && item.mingxiTaskIds && item.mingxiTaskIds.length > 0 ? (
                                expandedRadarTasks[item.id] && (
                                    <div className="mt-2 pl-2 border-l-2 border-slate-200 dark:border-slate-700 flex flex-col gap-1.5 animate-in slide-in-from-top-2">
                                        {item.mingxiTaskIds.map((obj, idx) => {
                                            const taskId = typeof obj === 'string' ? obj : obj.taskId;
                                            const rawEngine = typeof obj === 'string' ? '未知引擎' : obj.platform;
                                            const platformNames = { 'deepseek': 'DeepSeek', 'doubao': '豆包 (PC)', 'doubmb': '豆包 (MB)', 'txyb': '腾讯元宝 (PC)', 'ybmb': '腾讯元宝 (MB)', 'wenxin': '文心一言', 'kimi': 'Kimi', 'tyqw': '通义千问 (PC)', 'qwmb': '通义千问 (MB)', 'bdaipc': '百度 AI 搜索', 'xhs': '小红书' };
                                            const engine = platformNames[rawEngine] || rawEngine;
                                            return (
                                                <div key={idx} className="flex items-center gap-2 text-xs">
                                                    <span className="text-blue-600 dark:text-blue-400 font-medium whitespace-nowrap min-w-[60px]">{engine}</span>
                                                    <span className="text-slate-500 font-mono bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{taskId}</span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(taskId);
                                                            alert(`已复制单号: ${taskId}`);
                                                        }}
                                                        className="text-slate-400 hover:text-blue-500 transition-colors"
                                                        title="复制明悉任务号"
                                                    >
                                                        <Copy className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            ) : (
                                <div className="mt-1 text-xs text-slate-400 italic">API直连 (无RPA单号)</div>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-2">
                              {item.rpaStuckCount > 0 && (
                                <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded text-xs font-medium">
                                  ⚠️ {item.rpaStuckCount} 条 RPA 超时
                                </span>
                              )}
                              {item.aiStuckCount > 0 && (
                                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-medium">
                                  ⚠️ {item.aiStuckCount} 条 AI 死锁
                                </span>
                              )}
                              {item.aiFailedCount > 0 && (
                                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded text-xs font-medium">
                                  ⚠️ {item.aiFailedCount} 条 AI 解析失败
                                </span>
                              )}
                              {item.isProgressBug && (
                                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-xs font-medium">
                                  ⚠️ 进度条伪差卡死
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-slate-500">{new Date(item.createdAt).toLocaleString()}</td>
                          <td className="p-4 text-right">
                                                          <div className="flex justify-end gap-2">
                                {item.rpaStuckCount > 0 && (
                                  <a href="http://59.110.22.93:50101/" target="_blank" rel="noreferrer" className="text-orange-600 dark:text-orange-400 hover:underline text-xs bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded shadow-sm inline-flex items-center">RPA中转台处理</a>
                                )}
                                {item.aiFailedCount > 0 && (
                                  <button onClick={() => handleRetryFailed(item.id)} className="text-indigo-600 dark:text-indigo-400 hover:underline text-xs bg-indigo-50 dark:bg-indigo-900/20 px-2 py-1 rounded shadow-sm">清洗并重解析</button>
                                )}
                                <button onClick={() => handleReset(item.id)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">重置异常</button>
                                <button onClick={() => handleForceComplete(item.id)} className="text-white bg-red-500 hover:bg-red-600 text-xs px-2 py-1 rounded shadow-sm">强制结单</button>
                             </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/30">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      共 {allAlerts.length} 条记录，第 {currentPage} / {totalPages} 页
                    </span>
                    <div className="flex gap-2">
                      <button 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className="p-1.5 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-600 dark:text-slate-300"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className="p-1.5 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-600 dark:text-slate-300"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab Content: Matrix */}
      {activeTab === 'matrix' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 border-b dark:border-slate-700">
                <tr>
                  <th className="p-4 font-medium w-1/5">所属项目 / 编号</th>
                  <th className="p-4 font-medium w-1/6">主状态 / 进度</th>
                  <th className="p-4 font-medium w-[300px]">任务流转示意图</th>
                  <th className="p-4 font-medium">引擎明细状态</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-700 text-slate-700 dark:text-slate-300">
                {data.matrix.map(task => (
                  <React.Fragment key={task.id}>
                  <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                    <td className="p-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors" onClick={() => toggleTaskExpand(task.id)}>
                      <div className="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        {task.projectName}
                        {expandedMatrixTasks[task.id] ? <ChevronDown className="w-4 h-4 text-slate-400"/> : <ChevronRight className="w-4 h-4 text-slate-400"/>}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{task.taskNo}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${getStatusColor(task.status)}`}></span>
                        <span>{getStatusText(task.status)}</span>
                      </div>
                      <div className="w-full bg-slate-100 dark:bg-slate-700 h-1.5 rounded-full mt-2 overflow-hidden">
                        <div className="bg-blue-500 h-full rounded-full" style={{ width: `${task.progress}%` }}></div>
                      </div>
                    </td>
                    <td className="p-4">
                      <TaskFlowVisualizer status={task.status} fetchMode={task.fetchMode} />
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {task.engines.map((e, idx) => (
                          <div key={idx} title={`当前状态: ${getStatusText(e.status)}`} className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 px-2 py-1 rounded text-xs">
                            <span className={`w-2 h-2 rounded-full ${getStatusColor(e.status)}`}></span>
                            {e.engineId.replace('search_engine_', '')}
                            <span className="text-slate-400 ml-1">({getStatusText(e.status)})</span>
                          </div>
                        ))}
                        {task.engines.length === 0 && <span className="text-slate-400 italic text-xs">暂无分配引擎</span>}
                      </div>
                    </td>
                  </tr>
                  {expandedMatrixTasks[task.id] && (
                    <tr className="bg-slate-50/80 dark:bg-slate-900/50 border-b dark:border-slate-700/50">
                      <td colSpan="4" className="p-4">
                         <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 shadow-inner">
                            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-slate-700 dark:text-slate-200">
                               <ListChecks className="w-4 h-4" />
                               引擎任务层级明细 (Engine Level Tasks)
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                               {task.engines?.map((e, idx) => (
                                  <div key={idx} className="flex flex-col p-3 bg-slate-50 dark:bg-slate-900/80 rounded border border-slate-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                                     <div className="text-xs text-slate-500 mb-2 flex justify-between items-center">
                                        <div className="flex items-center gap-1.5">
                                          {e.mingxiTaskId ? (
                                            <>
                                              <span>RPA任务号: <span className="font-mono text-slate-800 dark:text-slate-200 font-medium">{e.mingxiTaskId}</span></span>
                                              <button onClick={() => copyToClipboard(e.mingxiTaskId)} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" title="复制明悉任务号">
                                                <Copy className="w-3 h-3" />
                                              </button>
                                            </>
                                          ) : (
                                            <span className="text-slate-400 italic">API直连 (无RPA单号)</span>
                                          )}
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${getStatusColor(e.status)} text-white shadow-sm`}>{getStatusText(e.status)}</span>
                                     </div>
                                     <div className="font-medium text-sm text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-2">
                                        {e.engineId.replace('search_engine_', '')}
                                     </div>
                                  </div>
                               ))}
                               {(!task.engines || task.engines.length === 0) && <div className="text-xs text-slate-400 col-span-full">暂无引擎任务数据</div>}
                            </div>
                         </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}